import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CONTENT, PALETTE, IMAGES } from "../../lib/content";
import { useLang } from "../../lib/LangContext";
import { ChapterHead, Reveal, ChapterShell } from "./Shell";
import { Wordmark, LogoLockup, MonogramSeal, MirrorR } from "../Logo";
import { TID } from "../../lib/testIds";

/* ---------- II. OPENING FRAGMENT ---------- */
export function OpeningChapter() {
    const { lang } = useLang();
    const c = CONTENT.opening[lang];
    return (
        <ChapterShell slug="opening" dark>
            <div className="col-span-12 md:col-span-10 md:col-start-2 min-h-[80vh] flex flex-col justify-center">
                <Reveal>
                    <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                        II — {c.eyebrow.toUpperCase()}
                    </div>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-10 md:mt-16 font-serif-lux italic text-3xl md:text-5xl lg:text-6xl leading-[1.15] max-w-5xl" style={{ color: "#F4EFE6" }}>
                        {c.body}
                    </p>
                </Reveal>
                <Reveal delay={0.4}>
                    <div className="mt-16 flex items-center gap-6">
                        <div className="h-px w-24" style={{ background: "#C9A96A" }} />
                        <span className="font-didone italic text-4xl" style={{ color: "#C9A96A", transform: "scaleX(-1)", display: "inline-block" }}>R</span>
                    </div>
                </Reveal>
            </div>
        </ChapterShell>
    );
}

/* ---------- III. PHILOSOPHY ---------- */
export function PhilosophyChapter() {
    const { lang } = useLang();
    const c = CONTENT.philosophy[lang];
    const pillars = [c.mission, c.vision, c.principle];
    return (
        <ChapterShell slug="philosophy">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="III" />
            </div>
            <div className="col-span-12 md:col-span-10 md:col-start-2 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-16">
                {pillars.map((p, i) => (
                    <Reveal key={i} delay={i * 0.15}>
                        <div className="border-t pt-6" style={{ borderColor: "rgba(201,169,106,0.5)" }}>
                            <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                                {`0${i + 1}`}
                            </div>
                            <h3 className="mt-4 font-didone text-3xl md:text-4xl">{p.label}</h3>
                            <p className="mt-6 font-serif-lux text-lg md:text-xl leading-relaxed opacity-90">{p.body}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </ChapterShell>
    );
}

/* ---------- IV. VALUES ---------- */
export function ValuesChapter() {
    const { lang } = useLang();
    const c = CONTENT.values[lang];
    return (
        <ChapterShell slug="values">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="IV" />
            </div>
            <div className="col-span-12 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                {c.items.map((it, i) => (
                    <Reveal key={i} delay={(i % 2) * 0.1}>
                        <div className="flex items-start gap-8 py-8 border-b" style={{ borderColor: "rgba(11,11,11,0.15)" }}>
                            <div className="font-didone italic text-2xl md:text-3xl w-16 shrink-0" style={{ color: "#C9A96A" }}>
                                {it.n}
                            </div>
                            <div>
                                <h4 className="font-didone text-3xl md:text-4xl">{it.label}</h4>
                                <p className="mt-3 font-serif-lux text-lg md:text-xl opacity-90">{it.body}</p>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </ChapterShell>
    );
}

/* ---------- V. MANIFESTO ---------- */
export function ManifestoChapter() {
    const { lang } = useLang();
    const c = CONTENT.manifesto[lang];
    return (
        <ChapterShell slug="manifesto">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="V" />
            </div>
            <div className="col-span-12 md:col-span-8 md:col-start-3 space-y-10">
                {c.body.map((line, i) => (
                    <Reveal key={i} delay={i * 0.05}>
                        <p className="font-serif-lux text-2xl md:text-3xl lg:text-4xl leading-[1.35]" style={{ hyphens: "auto" }}>
                            <span className="font-didone italic text-lg mr-4" style={{ color: "#C9A96A" }}>
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            {line}
                        </p>
                    </Reveal>
                ))}
            </div>
        </ChapterShell>
    );
}

/* ---------- VI. THE MIRRORED Я — HERO CHAPTER ---------- */
export function MirroredRChapter() {
    const { lang } = useLang();
    const c = CONTENT.mirroredR[lang];
    const ref = React.useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
    const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.15, 1, 0.9]);
    const rotate = useTransform(scrollYProgress, [0, 1], [-2, 2]);

    return (
        <section
            ref={ref}
            id="mirrored-r"
            data-testid={TID.chapter("mirrored-r")}
            data-chapter="mirrored-r"
            className="chapter relative min-h-[130vh] overflow-hidden"
            style={{ background: "#0B0B0B", color: "#F4EFE6" }}
        >
            {/* Massive parallax Я */}
            <motion.div
                data-testid={TID.mirrorHero}
                style={{ y, scale, rotate }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
            >
                <span
                    className="font-didone leading-none"
                    style={{
                        transform: "scaleX(-1)",
                        display: "inline-block",
                        fontSize: "min(90vw, 90vh)",
                        color: "#C9A96A",
                        opacity: 0.12,
                        fontWeight: 300,
                    }}
                >
                    R
                </span>
            </motion.div>

            {/* Foreground content */}
            <div className="relative column-12 pt-32 pb-32 min-h-[130vh] flex flex-col justify-center">
                <div className="col-span-12 md:col-span-11">
                    <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="VI" dark />
                </div>
                <div className="col-span-12 md:col-span-7 md:col-start-6 space-y-6 mt-4">
                    {c.block.map((line, i) => (
                        <Reveal key={i} delay={i * 0.12}>
                            <p className="font-serif-lux italic text-2xl md:text-3xl lg:text-4xl leading-[1.35]" style={{ color: "#F4EFE6" }}>
                                {line}
                            </p>
                        </Reveal>
                    ))}
                </div>
                <div className="col-span-12 md:col-span-4 md:col-start-2 mt-24">
                    <Reveal delay={0.3}>
                        <div className="border-t pt-6" style={{ borderColor: "rgba(201,169,106,0.5)" }}>
                            <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                                Ⅵ · {lang === "en" ? "NOTE" : "NOTA"}
                            </div>
                            <p className="mt-4 font-serif-lux text-lg opacity-80">{c.caption}</p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

/* ---------- VII. TONE OF VOICE ---------- */
export function VoiceChapter() {
    const { lang } = useLang();
    const c = CONTENT.voice[lang];
    return (
        <ChapterShell slug="tone-of-voice">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="VII" />
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-2">
                <Reveal>
                    <div className="font-micro text-[10px] mb-6" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                        {lang === "en" ? "WE DO" : "FACCIAMO"}
                    </div>
                    <ul className="space-y-6">
                        {c.do.map((d, i) => (
                            <li key={i} className="flex gap-4 border-b pb-4 font-serif-lux text-xl" style={{ borderColor: "rgba(11,11,11,0.15)" }}>
                                <span className="font-didone text-lg" style={{ color: "#C9A96A" }}>
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span>{d}</span>
                            </li>
                        ))}
                    </ul>
                </Reveal>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 mt-16 md:mt-0">
                <Reveal delay={0.15}>
                    <div className="font-micro text-[10px] mb-6" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                        {lang === "en" ? "WE DO NOT" : "NON FACCIAMO"}
                    </div>
                    <ul className="space-y-6">
                        {c.dont.map((d, i) => (
                            <li key={i} className="flex gap-4 border-b pb-4 font-serif-lux italic text-xl opacity-70" style={{ borderColor: "rgba(11,11,11,0.15)" }}>
                                <span className="font-didone text-lg not-italic" style={{ color: "#C9A96A" }}>
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span style={{ textDecoration: "line-through", textDecorationColor: "#C9A96A" }}>{d}</span>
                            </li>
                        ))}
                    </ul>
                </Reveal>
            </div>
        </ChapterShell>
    );
}

/* ---------- VIII. LOGO GUIDELINES ---------- */
export function LogoChapter() {
    const { lang } = useLang();
    const c = CONTENT.logo[lang];
    return (
        <ChapterShell slug="logo">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="VIII" />
            </div>

            {/* Primary big lockup */}
            <div className="col-span-12 mt-4">
                <Reveal>
                    <div className="border" style={{ borderColor: "rgba(201,169,106,0.5)" }}>
                        <LogoLockup variant="primary" payoff={lang === "en" ? "ITALIAN MAISON — RARE EDITIONS" : "MAISON ITALIANA — EDIZIONI RARE"} size="large" />
                    </div>
                </Reveal>
            </div>

            {/* Two column: inverted + prestige gold */}
            <div className="col-span-12 md:col-span-6 mt-10">
                <Reveal>
                    <div className="border" style={{ borderColor: "rgba(201,169,106,0.5)" }}>
                        <LogoLockup variant="inverted" payoff={lang === "en" ? "ITALIAN MAISON — RARE EDITIONS" : "MAISON ITALIANA — EDIZIONI RARE"} size="medium" />
                    </div>
                    <p className="mt-4 font-micro text-[10px]" style={{ letterSpacing: "0.4em" }}>
                        INVERTED · #0B0B0B
                    </p>
                </Reveal>
            </div>
            <div className="col-span-12 md:col-span-6 mt-10">
                <Reveal delay={0.1}>
                    <div className="border" style={{ borderColor: "rgba(201,169,106,0.5)" }}>
                        <LogoLockup variant="prestige" payoff={lang === "en" ? "ITALIAN MAISON — RARE EDITIONS" : "MAISON ITALIANA — EDIZIONI RARE"} size="medium" />
                    </div>
                    <p className="mt-4 font-micro text-[10px]" style={{ letterSpacing: "0.4em" }}>
                        PRESTIGE · Я #C9A96A
                    </p>
                </Reveal>
            </div>

            {/* Monogram seal */}
            <div className="col-span-12 md:col-span-6 mt-16">
                <Reveal>
                    <div className="border p-12 flex items-center justify-center" style={{ borderColor: "rgba(201,169,106,0.5)", background: "#F4EFE6" }}>
                        <MonogramSeal size={280} />
                    </div>
                    <p className="mt-4 font-micro text-[10px]" style={{ letterSpacing: "0.4em" }}>
                        MONOGRAM SEAL · IVORY
                    </p>
                </Reveal>
            </div>
            <div className="col-span-12 md:col-span-6 mt-16">
                <Reveal delay={0.1}>
                    <div className="border p-12 flex items-center justify-center" style={{ borderColor: "rgba(201,169,106,0.5)", background: "#0B0B0B" }}>
                        <MonogramSeal size={280} color="#F4EFE6" ring="#C9A96A" />
                    </div>
                    <p className="mt-4 font-micro text-[10px]" style={{ letterSpacing: "0.4em" }}>
                        MONOGRAM SEAL · WAX / NIGHT
                    </p>
                </Reveal>
            </div>

            {/* Variants table */}
            <div className="col-span-12 mt-24">
                <Reveal>
                    <div className="font-micro text-[10px] mb-6" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                        {lang === "en" ? "SYSTEM · SIX VARIANTS" : "SISTEMA · SEI VARIANTI"}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
                        {c.variants.map((v, i) => (
                            <div key={i} className="flex gap-6 border-b py-5" style={{ borderColor: "rgba(11,11,11,0.15)" }}>
                                <span className="font-didone italic text-lg w-12 shrink-0" style={{ color: "#C9A96A" }}>
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <div>
                                    <h4 className="font-didone text-2xl">{v.name}</h4>
                                    <p className="mt-2 font-serif-lux text-lg opacity-85">{v.use}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="mt-10 font-serif-lux italic text-xl opacity-80 max-w-3xl">{c.rule}</p>
                </Reveal>
            </div>
        </ChapterShell>
    );
}
